import { crmInformation } from '../store/slices/callingSlice';
import { setSidebarType } from '../store/slices/commonSlice';
import { isOnBreakNow } from './breakState';
import { popRedapp } from './redappPop';

// The single shared implementation of "fetch this call's CRM record and land the
// agent on the CRM screen".
//
// WHY THIS EXISTS
// There used to be four divergent copies of this logic (CallingSidebar, Crm,
// DialPadScreen, LeadPreview): only one retried, two navigated to /crm even inside an
// iframe, and only the socket-driven copy fetched at all reliably. A transient failure
// on any copy simply lost the landing, and nothing re-issued it. Every caller now
// routes through this one implementation so manual, preview, and socket-driven calls
// all land the same way.
//
// LANDING POLICY
// The goal is that a live call always ends up on the CRM screen so the agent can
// disposition it. We therefore land whenever the fetch RESOLVES:
//   - resolved WITH a record  -> land immediately.
//   - resolved WITHOUT a record (empty 200) -> the record may just not be ready yet
//     (a common race right as the call connects), so retry a few times; if it is
//     still empty after the retries, land anyway on a blank CRM. This matches the
//     legacy behavior of landing on any 2xx, so a genuinely record-less call is never
//     stranded off the CRM and the watchdog is not left polling it forever.
//   - every attempt REJECTED (network / timeout / 5xx) -> do NOT land; return false so
//     the caller/watchdog retries later. Landing on a blank CRM with no server contact
//     at all would just hide a real outage.
//
// Returns true once it has navigated (landed), false when it could not (on break, or
// every attempt rejected).
export const fetchCrmAndLand = async ({
  dispatch,
  navigate,
  data,
  maxAttempts = 3,
  retryDelayMs = 1500,
  log
}) => {
  const payload = {
    crm_id: data?.crm_id,
    unique_id: data?.unique_id
  };

  const land = () => {
    if (isOnBreakNow()) return false;
    dispatch(setSidebarType('CRM'));
    // Inside an iframe the host app renders the CRM, so we show a lightweight
    // "call in progress" placeholder instead of our (blank) CRM screen.
    navigate(window.self !== window.top ? '/call-in-progress' : '/crm');
    return true;
  };

  let anyResolved = false;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    // Never pull a customer record for an agent who is on, or entering, a break —
    // and re-check on every attempt, since retries can span several seconds.
    if (isOnBreakNow()) return false;
    try {
      const response = await dispatch(crmInformation(payload)).unwrap();
      anyResolved = true;
      log?.('try to fetch CRM', response);
      // `response` is the HTTP body (the axios interceptor unwraps to .data), so a
      // 2xx is always truthy; the record itself is `response.data`.
      if (response?.data) {
        // RedApp screen-pop. Runs here because this is the one point reached
        // exactly once per CONNECTED call (predictive, manual, preview alike)
        // and where the customer record — hence the primary number — is known.
        // No-op unless the agent's process has RedApp enabled.
        popRedapp(response.data, data, log);
        return land();
      }
    } catch (error) {
      log?.(`getCrmDetails error (attempt ${attempt}/${maxAttempts})`, String(error));
    }
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }
  }

  // Exhausted. Land anyway if the backend ever answered (record-less call); otherwise
  // report failure so the caller retries.
  if (anyResolved) {
    log?.('CRM landed without a record after retries', payload);
    return land();
  }
  log?.('CRM fetch failed after all retries', payload);
  return false;
};
